// M45b — Quick-access "Invite a friend" modal.
//
// Standalone surface optimized for "phone in hand, want to invite
// someone right now." Opens from the header AppBar icon or the
// sidebar entry via `useInvite().open()`. The full management surface
// (Invited-by line, Who-joined collapsible, Request-more dialog,
// quota counters) lives in the Settings "Invite friends" card — this
// modal has a "Manage invites" link that navigates there.
//
// States the modal renders:
//   1. loading  — fetching referral code on open
//   2. error    — fetch failed (rare; usually auth or network)
//   3. ready    — quota remaining > 0; show link + QR + copy buttons
//   4. exhausted — quota fully used; CTA to manage / request more
//
// Plausible events: referral_modal_opened (on open), and
// referral_link_copied / referral_qr_copied / referral_qr_downloaded
// for each share action. The data-fetch happens on first open and
// re-runs every subsequent open; with no caching the user always
// sees their current quota / uses count.

import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import { QRCodeCanvas } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import {
  getOrCreateMyReferralCode,
  referralRemaining,
  buildReferralLink,
  type ReferralCode,
} from '../lib/referrals';
import { trackEvent } from '../lib/analytics';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function InviteFriendsModal({ open, onClose }: Props) {
  const [code, setCode] = useState<ReferralCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch the user's referral code each time the modal opens — keeps
  // the quota / uses count fresh after a redemption lands while the
  // modal was closed.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    trackEvent('referral_modal_opened');
    getOrCreateMyReferralCode()
      .then((c) => {
        if (cancelled) return;
        setCode(c);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message || 'Could not load your invite link.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const url = code ? buildReferralLink(code.code) : '';
  const remaining = code ? referralRemaining(code) : 0;
  const exhausted = code != null && remaining === 0;

  function getCanvas(): HTMLCanvasElement | null {
    return qrContainerRef.current?.querySelector('canvas') ?? null;
  }

  async function copyLink() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setToast('Invite link copied');
      trackEvent('referral_link_copied');
    } catch {
      setToast('Could not copy — long-press to copy manually');
    }
  }

  async function copyQrImage() {
    const canvas = getCanvas();
    if (!canvas) return;
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );
      if (!blob) throw new Error('Could not render the QR image');
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setToast('QR code image copied');
      trackEvent('referral_qr_copied');
    } catch {
      setToast('Could not copy the QR image — use Download instead');
    }
  }

  function downloadQrImage() {
    const canvas = getCanvas();
    if (!canvas || !code) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `quarterstack-invite-${code.code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    trackEvent('referral_qr_downloaded');
  }

  function goToSettings() {
    onClose();
    // Settings card is anchored as "invite-friends" so the page can
    // scroll to it on landing. The card adds the element id; see
    // SettingsPage.tsx.
    navigate('/settings#invite-friends');
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Invite a friend to QuarterStack</DialogTitle>
        <DialogContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Exhausted state — replaces the share UI with a CTA to
              Settings. Same shell so the modal layout doesn't jump. */}
          {code && exhausted && (
            <Stack spacing={2} sx={{ py: 1 }}>
              <Typography variant="body1">
                You've used all <strong>{code.referral_quota}</strong> of your
                invites.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Want to invite more people? Open Settings to request a
                higher quota.
              </Typography>
            </Stack>
          )}

          {/* Ready state — link + inline QR + share buttons. */}
          {code && !exhausted && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Share this link or QR code with someone you trust.{' '}
                <strong>
                  {remaining} of {code.referral_quota}
                </strong>{' '}
                {remaining === 1 ? 'invite' : 'invites'} remaining.
              </Typography>

              <TextField
                fullWidth
                value={url}
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={copyLink}
                        edge="end"
                        aria-label="Copy invite link"
                        size="small"
                      >
                        <ContentCopyOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Inline QR — white backing keeps scan contrast in dark
                  mode. Sized for "scan with a phone across the table." */}
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  ref={qrContainerRef}
                  sx={{
                    display: 'inline-block',
                    p: 1.5,
                    bgcolor: '#fff',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    lineHeight: 0,
                  }}
                >
                  <QRCodeCanvas value={url} size={200} level="M" marginSize={2} />
                </Box>
              </Box>

              <Stack direction="row" spacing={1} justifyContent="center">
                <Button size="small" onClick={copyQrImage}>
                  Copy QR
                </Button>
                <Button size="small" onClick={downloadQrImage}>
                  Download QR
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button size="small" onClick={goToSettings}>
            Manage invites
          </Button>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </>
  );
}
